/**
 * FiguresEditor — the figures a text places by `[[fig:id]]` marker.
 *
 * One per text that can carry figures: the question stem, and each part
 * statement. Each figure is an image (upload, paste, or cut from the source
 * paper) plus its marker; "Place in text" appends the marker on a line of its
 * own, from where the author moves it by editing the text. A figure no marker
 * places shows after the text. Removing a figure removes its markers too, so
 * the text never names a figure that is gone.
 *
 * See docs/FeatureDesigns/question-rendering/question-figures-target.md.
 */
import { figureMarkerIds, stripFigureMarkers, type QuestionFigure, type QuestionImageSlot } from '@padcanvas/shared';
import { ImageUploadField } from '../ImageUploadField';

export interface FiguresEditorProps {
    /** The text the markers live in. */
    text: string;
    figures: readonly QuestionFigure[] | undefined;
    /** Text and figures change together — removing a figure edits both. */
    onChange: (text: string, figures: QuestionFigure[]) => void;
    onUploadImage: (base64: string, mimeType: string) => Promise<{ url: string }>;
    /** Which image slot a figure is, for "cut from paper". */
    slotFor: (figureId: string) => QuestionImageSlot;
    /** Ids already used elsewhere in the question (other parts, the stem) —
     *  ids are unique within a question. */
    takenIds?: readonly string[];
}

/** The next free `f<n>` id. */
export function nextFigureId(taken: readonly string[]): string {
    const used = new Set(taken);
    let n = 1;
    while (used.has(`f${n}`)) n++;
    return `f${n}`;
}

const marker = (id: string) => `[[fig:${id}]]`;

export function FiguresEditor({ text, figures, onChange, onUploadImage, slotFor, takenIds = [] }: FiguresEditorProps) {
    const list: QuestionFigure[] = [...(figures ?? [])];
    const placed = new Set(figureMarkerIds(text));
    const missing = [...placed].filter(id => !list.some(f => f.id === id));

    const add = () => {
        const id = nextFigureId([...takenIds, ...list.map(f => f.id)]);
        onChange(text, [...list, { id, url: '' }]);
    };
    const setUrl = (id: string, url: string | null) => {
        // A new image is new bytes: the old hash no longer describes it.
        onChange(text, list.map(f => (f.id === id ? { id: f.id, url: url ?? '', ...(f.alt ? { alt: f.alt } : {}) } : f)));
    };
    const remove = (id: string) => {
        onChange(stripFigureMarkers(text, new Set([id])), list.filter(f => f.id !== id));
    };
    const place = (id: string) => {
        const body = text.replace(/\s+$/, '');
        onChange(body ? `${body}\n${marker(id)}\n` : `${marker(id)}\n`, list);
    };
    const removeMissing = () => onChange(stripFigureMarkers(text, new Set(missing)), list);

    return (
        <div className="figures-editor">
            <div className="section-header">
                <label>Figures in the text</label>
                <button type="button" className="btn-add-inline" onClick={add}>+ Add figure</button>
            </div>
            {list.length === 0 && missing.length === 0 && (
                <p className="empty-note" style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                    For a figure between lines or inside a sentence. Write <code>[[fig:f1]]</code> in the
                    text where it goes — alone on a line for a full-width figure, inside a sentence for a
                    small one. A figure the text does not place shows after it.
                </p>
            )}
            {missing.length > 0 && (
                <p className="figures-editor-warning" role="alert" style={{ color: 'var(--color-danger, #dc2626)', fontSize: '0.85rem' }}>
                    The text names {missing.map(marker).join(', ')}, which {missing.length === 1 ? 'is' : 'are'} not
                    a figure here. Students would see "[figure missing]".{' '}
                    <button type="button" className="btn-link" onClick={removeMissing}>Remove from text</button>
                </p>
            )}
            {list.map(f => (
                <div key={f.id} className="figures-editor-row" data-testid={`figure-${f.id}`}>
                    <div className="figures-editor-row-head">
                        <code>{marker(f.id)}</code>
                        <span className="figures-editor-status">
                            {placed.has(f.id) ? 'placed in text' : 'shown after the text'}
                        </span>
                        {!placed.has(f.id) && (
                            <button type="button" className="btn-link" onClick={() => place(f.id)}>Place in text</button>
                        )}
                        <button
                            type="button"
                            className="btn-link"
                            onClick={() => { void navigator.clipboard?.writeText(marker(f.id)); }}
                            title="Copy the marker, to paste where the figure goes"
                        >
                            Copy marker
                        </button>
                        <button type="button" className="btn-remove-inline" onClick={() => remove(f.id)} title={`Remove ${f.id}`}>
                            x
                        </button>
                    </div>
                    <ImageUploadField
                        label={`Figure ${f.id}`}
                        value={f.url || null}
                        onChange={url => setUrl(f.id, url)}
                        maxPreviewHeight={160}
                        onUploadImage={onUploadImage}
                        sourceSlot={slotFor(f.id)}
                    />
                </div>
            ))}
        </div>
    );
}
